/*
 * Motor de movimiento de la ficha WMR.
 *
 * Reparto de tareas con wmr-movimiento.css: el CSS define COMO se mueve cada
 * cosa; este archivo decide QUE se mueve y CUANDO. Por eso aqui no hay
 * duraciones ni curvas.
 *
 * Punto importante: la clase `wmr-anim` del <html> es la que habilita los
 * estados ocultos del CSS, y se pone desde aqui. Si este archivo no llega a
 * ejecutarse, o el visitante pide menos movimiento, la clase no se añade y la
 * pagina se ve entera y quieta. Nunca se puede quedar contenido invisible por
 * un fallo de JavaScript.
 *
 * Los selectores son los reales de Horizon (slideshow-slide, variant-picker,
 * product-card...) mas los bloques propios `wmr-*`. No se toca ningun archivo
 * del tema para colgar atributos: todo se marca desde aqui.
 */

(() => {
  const raiz = document.documentElement;
  if (raiz.classList.contains('wmr-anim')) return;

  const menosMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (menosMovimiento.matches || !('IntersectionObserver' in window)) return;

  const SECCION = "[id^='shopify-section']:has(.wmr-encabezado)";
  const COLUMNA = '.product-details:has(.wmr-encabezado)';

  /*
   * Cada grupo lleva su propio contador, de modo que el escalonado se reinicia
   * en cada zona de la pagina. Dentro de un grupo el orden es el de la lista,
   * no el del DOM: asi el encabezado puede entrar antes que el precio aunque
   * comparta contenedor.
   *
   * `efecto: null` marca el elemento solo para saber cuando entra en pantalla,
   * sin ocultarlo (lo usa la galeria, donde quien se mueve es la <img>).
   */
  const GRUPOS = [
    [
      { sel: SECCION + ' slideshow-slide', efecto: null },
      { sel: SECCION + ' .slideshow-controls__thumbnail', efecto: 'lateral' },
    ],
    [
      { sel: COLUMNA + ' .wmr-encabezado__codigo', efecto: 'cortina-lat' },
      { sel: COLUMNA + ' .wmr-encabezado__titulo', efecto: 'cortina' },
      { sel: COLUMNA + ' .wmr-encabezado__colorway', efecto: 'subir' },
      { sel: COLUMNA + ' product-price', efecto: 'subir' },
      { sel: COLUMNA + ' variant-picker', efecto: 'subir' },
      { sel: COLUMNA + ' .wmr-guia', efecto: 'subir' },
      { sel: COLUMNA + ' .buy-buttons-block', efecto: 'subir' },
      { sel: COLUMNA + ' .wmr-acordeon', efecto: 'subir' },
    ],
    [{ sel: '.wmr-atributo', efecto: 'subir' }],
    [
      { sel: '.wmr-tela__titulo', efecto: 'cortina' },
      { sel: '.wmr-tela__parrafo', efecto: 'subir' },
      { sel: '.wmr-tela__video', efecto: 'cortina' },
      { sel: '.wmr-tela__boton', efecto: 'subir' },
    ],
    [{ sel: '.wmr-caracteristica', efecto: 'subir' }],
    [
      { sel: 'product-recommendations .text-block', efecto: 'cortina' },
      { sel: 'product-recommendations product-card', efecto: 'subir' },
    ],
  ];

  const observador = new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        if (!entrada.isIntersecting) continue;
        entrada.target.setAttribute('data-wmr-visible', '');
        observador.unobserve(entrada.target);
      }
    },
    // Un margen negativo abajo evita que algo se de por revelado cuando apenas
    // asoma un pixel por el borde inferior.
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  function marcar() {
    for (const grupo of GRUPOS) {
      let i = 0;
      for (const { sel, efecto } of grupo) {
        for (const el of document.querySelectorAll(sel)) {
          if (el.hasAttribute('data-wmr-visible') || el.dataset.wmrMarcado) continue;
          el.dataset.wmrMarcado = '1';
          if (efecto) el.setAttribute('data-wmr-anim', efecto);
          el.style.setProperty('--wmr-i', i);
          observador.observe(el);
          i += 1;
        }
      }
    }

    document.querySelectorAll('.wmr-guia__tabla tbody tr').forEach((fila, n) => {
      fila.style.setProperty('--wmr-i', n);
    });
  }

  let marcadoPendiente = false;

  function programarMarcado() {
    if (marcadoPendiente) return;
    marcadoPendiente = true;
    requestAnimationFrame(() => {
      marcadoPendiente = false;
      marcar();
    });
  }

  /*
   * Contenido que llega despues de la carga. Los recomendados de Horizon
   * pintan cuatro esqueletos y traen las tarjetas reales por fetch, asi que en
   * el primer marcado todavia no existen. El observador solo mira inserciones
   * (`childList`), no atributos, de modo que el propio `marcar()` no lo vuelve
   * a disparar.
   */
  const vigilante = new MutationObserver((mutaciones) => {
    for (const mutacion of mutaciones) {
      for (const nodo of mutacion.addedNodes) {
        if (nodo.nodeType === 1) {
          programarMarcado();
          return;
        }
      }
    }
  });

  /*
   * Red de seguridad. Si algo queda observado pero nunca se dispara (un
   * contenedor con display:none al cargar, una pestaña en segundo plano que
   * no llega a componer), a los 5 s se revela igualmente. Es preferible
   * perder la animacion a perder el contenido.
   */
  function rescatar() {
    document.querySelectorAll('[data-wmr-anim]:not([data-wmr-visible])').forEach((el) => {
      el.setAttribute('data-wmr-visible', '');
      observador.unobserve(el);
    });
  }

  /* ---------------------------------------------------------------- *
   * Acordeones
   *
   * `<details>` no anima su apertura. Donde el navegador entiende
   * `interpolate-size`, la transicion la lleva el CSS sobre
   * `::details-content`. Donde no, se anima aqui con Web Animations y se marca
   * el elemento con `data-wmr-altura` para que el CSS se aparte.
   * ---------------------------------------------------------------- */

  const CURVA = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const soportaAltura = CSS.supports('interpolate-size', 'allow-keywords');

  function prepararAcordeones() {
    if (soportaAltura) return;
    document.querySelectorAll('.wmr-acordeon').forEach((det) => {
      if (det.tagName === 'DETAILS') det.setAttribute('data-wmr-altura', '');
    });
  }

  function animarAcordeon(evento) {
    if (soportaAltura) return;

    const resumen = evento.target.closest('.wmr-acordeon__resumen');
    if (!resumen || resumen.tagName !== 'SUMMARY') return;

    const det = resumen.closest('details.wmr-acordeon');
    const cuerpo = det && det.querySelector('.wmr-acordeon__cuerpo');
    if (!det || !cuerpo) return;

    evento.preventDefault();
    if (det.dataset.wmrAnimando) return;
    det.dataset.wmrAnimando = '1';

    const abriendo = !det.open;
    if (abriendo) det.open = true;

    const alto = cuerpo.scrollHeight;
    const fotogramas = [{ height: '0px', opacity: 0 }, { height: alto + 'px', opacity: 1 }];

    const animacion = cuerpo.animate(abriendo ? fotogramas : fotogramas.slice().reverse(), {
      duration: abriendo ? 420 : 340,
      easing: CURVA,
    });

    animacion.finished
      .catch(() => {})
      .then(() => {
        if (!abriendo) det.open = false;
        delete det.dataset.wmrAnimando;
      });
  }

  /* ---------------------------------------------------------------- *
   * Cambio de variante
   *
   * Horizon solo re-renderiza el <variant-picker>, asi que el colorway y el
   * precio cambian de contenido sin ninguna señal visual. El relevo marca ese
   * cambio con un parpadeo corto.
   * ---------------------------------------------------------------- */

  function relevo(el) {
    if (!el) return;
    el.removeAttribute('data-wmr-cambio');
    // Fuerza el reinicio de la animacion si ya estaba puesta.
    void el.offsetWidth;
    el.setAttribute('data-wmr-cambio', '');
    el.addEventListener('animationend', () => el.removeAttribute('data-wmr-cambio'), { once: true });
  }

  function alCambiarVariante(evento) {
    if (!evento.target.closest('input[data-option-name]')) return;
    relevo(document.querySelector('.wmr-encabezado__colorway'));
    relevo(document.querySelector(COLUMNA + ' product-price'));
    // El picker se re-renderiza: puede traer opciones nuevas sin marcar.
    programarMarcado();
  }

  raiz.classList.add('wmr-anim');
  marcar();
  prepararAcordeones();

  vigilante.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('click', animarAcordeon);
  document.addEventListener('change', alCambiarVariante);
  window.setTimeout(() => {
    rescatar();
    // Pasado este punto ya no llega contenido diferido; se deja de vigilar para
    // no cargar con un observador de todo el <body> el resto de la sesion.
    vigilante.disconnect();
  }, 5000);

  // Las secciones que el editor de temas vuelve a pintar llegan sin marcar.
  document.addEventListener('shopify:section:load', () => {
    marcar();
    prepararAcordeones();
  });
})();
